import { DOCUMENT } from '@angular/common';
import { Injectable, computed, inject, signal } from '@angular/core';

export type ThemeMode = 'light' | 'dark';

@Injectable({ providedIn: 'root' })
export class ThemeService {
  private readonly document = inject(DOCUMENT);
  private readonly storageKey = 'fruitage-theme';
  private readonly darkModeClass = 'dark-mode';

  private readonly preferredMode = signal<ThemeMode>(this.getInitialMode());

  constructor() {
    this.applyTheme(this.preferredMode());
  }

  readonly mode = this.preferredMode.asReadonly();
  readonly usingDarkMode = computed(() => this.preferredMode() === 'dark');

  setTheme(mode: ThemeMode) {
    if (mode !== 'light' && mode !== 'dark') {
      return;
    }

    if (this.preferredMode() === mode) {
      return;
    }

    this.preferredMode.set(mode);
    this.persistMode(mode);
    this.applyTheme(mode);
  }

  toggleDarkMode(enabled: boolean) {
    this.setTheme(enabled ? 'dark' : 'light');
  }

  private applyTheme(mode: ThemeMode) {
    const root = this.document?.documentElement;
    if (!root) {
      return;
    }
    root.classList.toggle(this.darkModeClass, mode === 'dark');
  }

  private persistMode(mode: ThemeMode) {
    if (typeof window === 'undefined') {
      return;
    }
    try {
      window.localStorage.setItem(this.storageKey, mode);
    } catch {
      // Ignore storage failures (e.g., in private browsing mode)
    }
  }

  private getInitialMode(): ThemeMode {
    if (typeof window === 'undefined') {
      return 'dark';
    }

    try {
      const stored = window.localStorage.getItem(this.storageKey);
      if (stored === 'light' || stored === 'dark') {
        return stored;
      }
    } catch {
      // Ignore
    }

    const prefersDark = window.matchMedia?.('(prefers-color-scheme: dark)')?.matches ?? false;
    return prefersDark ? 'dark' : 'light';
  }
}
